<script setup>
import AccordionItem from './AccordionItem.vue';
import EntityFields from './EntityFields.vue';
import { toRefs, computed } from 'vue';

const props = defineProps({
  relation: Object,
  parent: Object,
  parentMovie: Object,
  schema: Object,
  openAccordionIds: Array,
  openEntityModal: Function,
  onDeleteEntity: Function
});
const emit = defineEmits(['update']);

const { relation, parent, parentMovie, schema, openAccordionIds, openEntityModal, onDeleteEntity } = toRefs(props);

let selectedId = '';

const allEntities = computed(() => {
  if (!parentMovie.value || !relation.value || !relation.value.type) {
    return [];
  }
  return Array.isArray(parentMovie.value[relation.value.type])
    ? parentMovie.value[relation.value.type]
    : Array.isArray(parentMovie.value[relation.value.type.toLowerCase()])
      ? parentMovie.value[relation.value.type.toLowerCase()]
      : [];
});

const relationIds = computed(() => {
  const ids = parent.value[relation.value.name];
  if (relation.value.many) {
    return Array.isArray(ids) ? ids : [];
  } else {
    return ids || null;
  }
});

const relationSchema = computed(() => schema.value[relation.value.type]);

const validRelationIds = computed(() => {
  if (relation.value.many) {
    return (Array.isArray(relationIds.value) ? relationIds.value : []).filter((id) => typeof id === 'string' && allEntities.value.some((e) => e.id === id));
  } else {
    return typeof relationIds.value === 'string' && allEntities.value.find((e) => e.id === relationIds.value) ? relationIds.value : null;
  }
});

const singleEntity = computed(() => {
  if (!relation.value.many && validRelationIds.value) {
    return allEntities.value.find((e) => e.id === validRelationIds.value) || null;
  }
  return null;
});

const singleEntityTitle = computed(() => {
  if (!singleEntity.value) return null;
  const fields = relationSchema.value && relationSchema.value.fields;
  if (fields && fields.length && singleEntity.value[fields[0].name]) {
    return singleEntity.value[fields[0].name];
  }
  return singleEntity.value.id || null;
});

async function addSelected() {
  if (selectedId && !relationIds.value.includes(selectedId)) {
    parent.value[relation.value.name] = parent.value[relation.value.name] || [];
    parent.value[relation.value.name].push(selectedId);
    await emit('update', parentMovie.value);
    selectedId = '';
  }
}

async function setSelected() {
  if (selectedId) {
    parent.value[relation.value.name] = selectedId;
    await emit('update', parentMovie.value);
  }
}

function getRandomId() {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
}

async function createEntity(data) {
  data.id = getRandomId();
  if (!Array.isArray(parentMovie.value[relation.value.type])) {
    parentMovie.value[relation.value.type] = [];
  }
  parentMovie.value[relation.value.type].push(data);
  if (relation.value.many) {
    parent.value[relation.value.name] = parent.value[relation.value.name] || [];
    parent.value[relation.value.name].push(data.id);
  } else {
    parent.value[relation.value.name] = data.id;
  }
  await emit('update', parentMovie.value);
}

async function updateEntity(item, data) {
  const id = item.id;
  const arr = parentMovie.value[relation.value.type];
  if (Array.isArray(arr)) {
    const idx = arr.findIndex((e) => e.id === id);
    if (idx !== -1) {
      arr[idx] = { ...arr[idx], ...data, id };
    }
  }
  await emit('update', parentMovie.value);
}

function removeRelation(id) {
  const idx = parent.value[relation.value.name].indexOf(id);
  if (idx !== -1) {
    parent.value[relation.value.name].splice(idx, 1);
    emit('update', parentMovie.value);
  }
}

function clearRelation() {
  parent.value[relation.value.name] = null;
  emit('update', parentMovie.value);
}

function removeEntity(id, type) {
  if (parent.value !== parentMovie.value) {
    return;
  }
  const arr = parentMovie.value[type];
  if (!Array.isArray(arr)) return;
  // Check for references elsewhere in the movie
  const usages = [];
  const checkRefs = (obj, path = []) => {
    if (Array.isArray(obj)) {
      obj.forEach((item, idx) => checkRefs(item, path.concat(idx)));
    } else if (obj && typeof obj === 'object') {
      Object.entries(obj).forEach(([key, value]) => {
        if (key === relation.value.name && parent.value !== parentMovie.value) return;
        if (Array.isArray(value) && value.includes(id)) {
          usages.push(`${path.concat(key).join('.')} (array)`);
        } else if (value === id) {
          usages.push(`${path.concat(key).join('.')}`);
        } else {
          checkRefs(value, path.concat(key));
        }
      });
    }
  };
  checkRefs(parentMovie.value);
  const filteredUsages = usages.filter((u) => !u.startsWith(`${relation.value.type}`));
  let proceed = true;
  if (filteredUsages.length) {
    const msg = `This ${relation.value.type} is used in:\n${filteredUsages.join('\n')}\n\nAre you sure you want to remove it?`;
    proceed = window.confirm(msg);
    if (proceed) {
      const removeRefs = (obj) => {
        if (Array.isArray(obj)) {
          for (let i = obj.length - 1; i >= 0; i--) {
            if (obj[i] === id) {
              obj.splice(i, 1);
            } else {
              removeRefs(obj[i]);
            }
          }
        } else if (obj && typeof obj === 'object') {
          Object.entries(obj).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              obj[key] = value.filter((v) => v !== id);
              removeRefs(obj[key]);
            } else if (value === id && key !== 'id') {
              obj[key] = null;
            } else {
              removeRefs(value);
            }
          });
        }
      };
      removeRefs(parentMovie.value);
    }
  }
  if (proceed) {
    const idx = arr.findIndex((e) => e.id === id);
    if (idx !== -1) {
      arr.splice(idx, 1);
      emit('update', parentMovie.value);
      if (onDeleteEntity.value) {
        onDeleteEntity.value(type, id);
      }
    }
  }
}

function createNestedEntity(nestedRelation, parent, parentMovie, data) {
  data.id = getRandomId();
  if (!Array.isArray(parentMovie[nestedRelation.type])) {
    parentMovie[nestedRelation.type] = [];
  }
  parentMovie[nestedRelation.type].push(data);
  if (nestedRelation.many) {
    parent[nestedRelation.name] = parent[nestedRelation.name] || [];
    parent[nestedRelation.name].push(data.id);
  } else {
    parent[nestedRelation.name] = data.id;
  }
  emit('update', parentMovie);
}

async function cleanupInvalidRelations() {
  if (parent.value === parentMovie.value) return;
  if (relation.value.many) {
    const validIds = relationIds.value.filter((id) => {
      if (typeof id === 'object' && id !== null) return true;
      return typeof id === 'string' && allEntities.value.find((e) => e.id === id);
    });
    if (validIds.length !== relationIds.value.length) {
      parent.value[relation.value.name] = validIds;
      await emit('update', parentMovie.value);
    }
  } else if (typeof relationIds.value === 'string' && !allEntities.value.find((e) => e.id === relationIds.value)) {
    parent.value[relation.value.name] = null;
    await emit('update', parentMovie.value);
  }
}
</script>

<template>
  <div class="entity-section">
    <!-- Add New Button at the top (only for many relations) -->
    <!-- Many Relations -->
    <div v-if="relation.many">
      <div v-if="parent === parentMovie ? allEntities.length : validRelationIds.length">
        <template v-for="entity in parent === parentMovie ? allEntities : validRelationIds.map((id) => allEntities.find((e) => e.id === id)).filter((e) => e && typeof e === 'object')">
          <AccordionItem
            v-if="entity && (relationSchema.fields.length ? entity[relationSchema.fields[0].name] || entity.id : entity.id)"
            :key="entity.id"
            :title="relationSchema.fields.length ? entity[relationSchema.fields[0].name] || entity.id : entity.id"
            :id="relation.type + '-' + entity.id"
          >
            <template #header-actions>
              <button @click="openEntityModal('edit', relation.type, entity, parent, parentMovie, relation.name, (data) => updateEntity(entity, data))" class="icon">edit</button>
              <button v-if="parent === parentMovie" @click="removeEntity(entity.id, relation.type)" class="icon">delete</button>
              <button v-else @click="removeRelation(entity.id)" class="icon">delete</button>
            </template>
            <EntityFields class="entity-fields" :entity="entity" :fields="relationSchema.fields" />
            <!-- Nested Relations for this entity as accordions -->
            <div v-if="relationSchema.relations" class="nested-relations">
              <AccordionItem
                v-for="nestedRelation in relationSchema.relations"
                :key="nestedRelation.name + '-' + entity.id"
                :title="nestedRelation.name.charAt(0).toUpperCase() + nestedRelation.name.slice(1)"
                :id="nestedRelation.type + '-' + entity.id"
              >
                <template #header-actions>
                  <button
                    @click.stop="openEntityModal('add', nestedRelation.type, null, entity, parentMovie, nestedRelation.name, (data) => createNestedEntity(nestedRelation, entity, parentMovie, data))"
                    class="icon"
                  >
                    library_add
                  </button>
                </template>
                <RelationSection
                  :relation="nestedRelation"
                  :parent="entity"
                  :parent-movie="parentMovie"
                  :schema="schema"
                  :open-accordion-ids="openAccordionIds"
                  :open-entity-modal="openEntityModal"
                  @update="$emit('update', parentMovie)"
                />
              </AccordionItem>
            </div>
          </AccordionItem>
        </template>
      </div>
      <div v-else class="warning">No {{ relation.name }} added yet</div>
    </div>
    <!-- Single Relations (always show select) -->
    <div v-if="!relation.many">
      <AccordionItem v-if="singleEntity && singleEntityTitle" :title="singleEntityTitle" :id="relation.type + '-' + singleEntity.id">
        <template #header-actions>
          <button @click="openEntityModal('edit', relation.type, singleEntity, parent, parentMovie, relation.name, (data) => updateEntity(singleEntity, data))">Edit</button>
        </template>
        <EntityFields :entity="singleEntity" :fields="relationSchema.fields" />
        <!-- Nested Relations for single entity as accordions -->
        <div v-if="relationSchema.relations">
          <AccordionItem
            v-for="nestedRelation in relationSchema.relations"
            :key="nestedRelation.name + '-' + singleEntity.id"
            :title="nestedRelation.name.charAt(0).toUpperCase() + nestedRelation.name.slice(1)"
            :id="nestedRelation.type + '-' + singleEntity.id"
          >
            <template #header-actions>
              <button
                @click.stop="
                  openEntityModal('add', nestedRelation.type, null, singleEntity, parentMovie, nestedRelation.name, (data) => createNestedEntity(nestedRelation, singleEntity, parentMovie, data))
                "
              >
                Add
              </button>
            </template>
            <RelationSection
              :relation="nestedRelation"
              :parent="singleEntity"
              :parent-movie="parentMovie"
              :schema="schema"
              :open-accordion-ids="openAccordionIds"
              :open-entity-modal="openEntityModal"
              @update="$emit('update', parentMovie)"
            />
          </AccordionItem>
        </div>
      </AccordionItem>
    </div>
  </div>
</template>
